import { Injectable, Optional, inject } from '@angular/core';
import { Analytics } from '@angular/fire/analytics';
import {
  ANALYTICS_LOG_EVENT,
  ANALYTICS_SET_USER_PROPERTIES,
} from '../tokens/analytics.token';
import {
  AccommodationCreatedParams,
  AccommodationSubmittedParams,
  AddFundsFailedParams,
  AddFundsStartParams,
  AnalyticsUserProperties,
  BeginCheckoutParams,
  BookingConfirmedParams,
  AvailabilityRequestParams,
  BookingFailedParams,
  BookingStartedParams,
  LoginParams,
  MemberApprovedParams,
  PurchaseParams,
  SelectItemParams,
  SignUpParams,
  ViewItemListParams,
  ViewItemParams,
} from './analytics.events';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly logEventFn = inject(ANALYTICS_LOG_EVENT);
  private readonly setUserPropertiesFn = inject(ANALYTICS_SET_USER_PROPERTIES);

  logSignUp(params: SignUpParams): void {
    this.dispatch('sign_up', params);
  }

  logLogin(params: LoginParams): void {
    this.dispatch('login', params);
  }

  logCompleteProfile(): void {
    this.dispatch('complete_profile');
  }

  logAccommodationCreated(params: AccommodationCreatedParams): void {
    this.dispatch('accommodation_created', params);
  }

  logAccommodationSubmitted(params: AccommodationSubmittedParams): void {
    this.dispatch('accommodation_submitted', params);
  }

  logMemberApproved(params: MemberApprovedParams): void {
    this.dispatch('member_approved', params);
  }

  logViewItemList(params: ViewItemListParams): void {
    this.dispatch('view_item_list', params);
  }

  logViewItem(params: ViewItemParams): void {
    this.dispatch('view_item', params);
  }

  logSelectItem(params: SelectItemParams): void {
    this.dispatch('select_item', params);
  }

  /** Real-money checkout (GetNet card top-up). Bookings use {@link logBookingStarted}. */
  logBeginCheckout(params: BeginCheckoutParams): void {
    this.dispatch('begin_checkout', params);
  }

  /** Real-money conversion (GetNet card top-up). Bookings use {@link logBookingConfirmed}. */
  logPurchase(params: PurchaseParams): void {
    this.dispatch('purchase', params);
  }

  logBookingStarted(params: BookingStartedParams): void {
    this.dispatch('booking_started', params);
  }

  logBookingConfirmed(params: BookingConfirmedParams): void {
    this.dispatch('booking_confirmed', params);
  }

  logBookingFailed(params: BookingFailedParams): void {
    this.dispatch('booking_failed', params);
  }

  /** El socio abrió el formulario de solicitud de disponibilidad. */
  logAvailabilityRequestStarted(params: AvailabilityRequestParams): void {
    this.dispatch('availability_request_started', params);
  }

  /** La solicitud de disponibilidad se envió correctamente. */
  logAvailabilityRequestSent(params: AvailabilityRequestParams): void {
    this.dispatch('availability_request_sent', params);
  }

  logAddFundsStart(params: AddFundsStartParams): void {
    this.dispatch('add_funds_start', params);
  }

  logAddFundsFailed(params: AddFundsFailedParams): void {
    this.dispatch('add_funds_failed', params);
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    if (!this.analytics) {
      console.debug('[analytics:noop] setUserProperties', properties);
      return;
    }
    this.setUserPropertiesFn(this.analytics, { ...properties });
  }

  private dispatch(name: string, params?: object): void {
    if (!this.analytics) {
      console.debug('[analytics:noop]', name, params);
      return;
    }
    this.logEventFn(this.analytics, name, params as Record<string, unknown> | undefined);
  }
}
